import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useGuardedCallback } from '../permissions/useGuardedAction';
import {
  PermissionLevel,
  PermissionResource,
} from '../permissions/permission-catalog';
import AsyncButton from '../components/AsyncButton';
import type {
  WorkspaceAllocationRequest,
  WorkspaceCapabilities,
  WorkspaceResourceRequestDetail,
} from '../client';
import { useApi } from '../state/ApiContext';
import {
  formatCpu,
  formatDecimal,
  formatMemory,
  formatStorage,
} from './WorkspacePolicySettings';
import './workspace-resources.css';
import { useApprovalEvents } from '../state/ApprovalEventsContext';
import { ApprovalCardHeader } from '../approvals/ApprovalCardHeader';
import '../approvals/approvals.css';

interface WorkspaceApprovalCardProps {
  teamId: number | string;
  token: string;
  /**
   * Omits the card's own agent/origin header — used when a wrapper (e.g. the
   * team-home `ApprovalRequestView`) already renders it, to avoid a doubled
   * header. Mirrors `InviteApprovalCard`'s `headerless` prop.
   */
  headerless?: boolean;
}

type ViewMode = 'summary' | 'edit';
type DecisionPending = 'approved' | 'rejected' | null;

type Allocation = Omit<WorkspaceAllocationRequest, 'gpu'> & {
  gpu?: { type?: string; count?: number } | null;
};

const STATUS_LABELS: Record<string, string> = {
  pending: 'Pending approval',
  approved: 'Approved',
  rejected: 'Rejected',
  canceled: 'Canceled',
  provisioning: 'Provisioning',
  fulfilled: 'Provisioned',
  failed: 'Failed',
};

const allocationFrom = (
  detail: WorkspaceResourceRequestDetail,
): Allocation => ({
  ...(detail.revision.normalizedAllocation as Allocation),
});

const sameAllocation = (left: Allocation | null, right: Allocation | null) =>
  JSON.stringify(left) === JSON.stringify(right);

const errorStatus = (error: unknown): number | undefined =>
  (error as { response?: { status?: number } })?.response?.status;

const errorMessage = (error: unknown): string | undefined => {
  const message = (error as { response?: { data?: { error?: unknown } } })
    ?.response?.data?.error;
  return typeof message === 'string' && message.trim() ? message : undefined;
};

type WorkspaceQuote = {
  total30DayUsd?: string | number;
  projectionDays?: number;
  projectionHoursPerDay?: number;
  label?: string;
  lineItems?: Array<{ kind?: string; total30DayUsd?: string }>;
};

// A single dense summary line for the compact "summary" step, e.g.
// "Workspace requested · 1 CPU · 2 GB RAM · 10 GB disk · No GPU ·
// Network access · $0.23/hr".
export const describeAllocation = (
  allocation: Allocation,
  quote: WorkspaceQuote,
): string => {
  const cpu = formatDecimal(Number(allocation.cpuMillis || 0) / 1000);
  const ram = formatDecimal(Number(allocation.memoryMiB || 0) / 1024);
  const disk = formatDecimal(Number(allocation.storageGiB || 0));
  const gpu = allocation.gpu
    ? `${allocation.gpu.count || 1}× ${allocation.gpu.type}`
    : 'No GPU';
  const network =
    allocation.networkMode === 'allowlisted_egress'
      ? 'Network access'
      : 'No network';
  const total = Number(quote.total30DayUsd || 0);
  const hours =
    Number(quote.projectionDays || 0) *
    Number(quote.projectionHoursPerDay || 0);
  const price =
    total > 0 && hours > 0
      ? `$${(total / hours).toFixed(2)}/hr`
      : 'No provider charge';
  return `Workspace requested · ${cpu} CPU · ${ram} GB RAM · ${disk} GB disk · ${gpu} · ${network} · ${price}`;
};

// Snaps a value to the nearest multiple of the provider capability's step,
// clamped within [min, max]. Applied to the CPU/memory/storage sliders so a
// dragged or programmatically-set value can never land between the
// provider's real increments before it reaches the PATCH.
const snapToStep = (
  value: number,
  range: { min: number; max: number; step: number },
) => {
  if (!range?.step) return value;
  const steps = Math.round((value - range.min) / range.step);
  const snapped = range.min + steps * range.step;
  return Math.min(range.max, Math.max(range.min, snapped));
};

export const WorkspaceApprovalCard: React.FC<WorkspaceApprovalCardProps> = ({
  teamId,
  token,
  headerless = false,
}) => {
  const { workspacesApi, approvalsApi } = useApi();
  const { epoch: approvalEpoch } = useApprovalEvents();
  const numericTeamId = Number(teamId);
  const [detail, setDetail] = useState<WorkspaceResourceRequestDetail | null>(
    null,
  );
  const [capabilities, setCapabilities] =
    useState<WorkspaceCapabilities | null>(null);
  const [draft, setDraft] = useState<Allocation | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [decisionPending, setDecisionPending] = useState<DecisionPending>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [decisionNote, setDecisionNote] = useState('');
  const [retryNonce, setRetryNonce] = useState(0);
  const [mode, setMode] = useState<ViewMode>('summary');
  const [confirming, setConfirming] = useState<'approve' | 'reject' | null>(
    null,
  );
  const serverAllocation = useRef<Allocation | null>(null);
  const editVersion = useRef(0);
  const lifecycleEpoch = useRef(0);
  const debounceTimer = useRef<number | null>(null);
  const patchPromise = useRef<Promise<void> | null>(null);
  const decisionInFlight = useRef(false);
  const deciding = decisionPending !== null;

  const load = useCallback(
    async (stale = false, preserveIfEditedAfter?: number) => {
      const response =
        await workspacesApi.apiTeamsTeamIdWorkspaceRequestsTokenGet(
          numericTeamId,
          token,
        );
      const nextDetail = response.data;
      const nextAllocation = allocationFrom(nextDetail);
      serverAllocation.current = nextAllocation;
      setDetail(nextDetail);
      if (
        preserveIfEditedAfter === undefined ||
        editVersion.current === preserveIfEditedAfter
      ) {
        setDraft(nextAllocation);
      }
      if (stale)
        setNotice('A newer revision was loaded. Review it before deciding.');

      const providerKey = nextDetail.revision.providerKey as
        'local' | 'gke' | 'firecracker';
      const capabilityResponse =
        await workspacesApi.apiTeamsTeamIdWorkspaceProvidersProviderKeyCapabilitiesGet(
          numericTeamId,
          providerKey,
          nextDetail.revision.providerRegion,
        );
      setCapabilities(capabilityResponse.data.capabilities);
      return nextDetail;
    },
    [numericTeamId, token, workspacesApi],
  );

  useEffect(() => {
    let current = true;
    setLoading(true);
    load()
      .catch(
        () => current && setError('Could not load this workspace request.'),
      )
      .finally(() => current && setLoading(false));
    return () => {
      current = false;
    };
  }, [load, approvalEpoch]);

  useEffect(() => {
    if (
      !detail ||
      detail.request.status !== 'pending' ||
      !draft ||
      deciding ||
      sameAllocation(draft, serverAllocation.current)
    ) {
      return;
    }
    const retryAttempt = retrying;
    const timer = window.setTimeout(() => {
      debounceTimer.current = null;
      const submittedEditVersion = editVersion.current;
      const startedEpoch = lifecycleEpoch.current;
      const task = (async () => {
        setSaving(true);
        setError(null);
        setNotice(null);
        try {
          await workspacesApi.apiTeamsTeamIdWorkspaceRequestsTokenPatch(
            numericTeamId,
            token,
            {
              revision: detail.request.currentRevision,
              allocation: draft as unknown as WorkspaceAllocationRequest,
            },
          );
          if (startedEpoch === lifecycleEpoch.current) {
            await load(false, submittedEditVersion);
          }
        } catch (requestError) {
          if (startedEpoch !== lifecycleEpoch.current) return;
          if (errorStatus(requestError) === 409) {
            await load(true, submittedEditVersion);
          } else {
            setError(
              'Could not update the resource request. Retry the update.',
            );
          }
        } finally {
          if (startedEpoch === lifecycleEpoch.current) {
            setSaving(false);
          }
          if (retryAttempt) setRetrying(false);
        }
      })();
      patchPromise.current = task;
      const clearTask = () => {
        if (patchPromise.current === task) patchPromise.current = null;
      };
      task.then(clearTask, clearTask);
    }, 250);
    debounceTimer.current = timer;
    return () => {
      window.clearTimeout(timer);
      if (debounceTimer.current === timer) {
        debounceTimer.current = null;
        if (retryAttempt) setRetrying(false);
      }
    };
  }, [
    deciding,
    detail,
    draft,
    load,
    numericTeamId,
    retryNonce,
    token,
    workspacesApi,
  ]);

  /**
   * Approving a workspace request is `approval: manage`, the same grant as any
   * other approval decision — rejecting included, because declining is as much
   * an exercise of the decision as granting.
   */
  const decideGuard = useGuardedCallback<
    [decision: 'approve' | 'reject' | null]
  >({
    resource: PermissionResource.APPROVAL,
    level: PermissionLevel.MANAGE,
    verb: 'decide',
    subject: 'workspace requests',
    testId: 'workspace-approval-decide-denied',
    onAllowed: (decision) => void decide(decision as never),
  });

  const decide = async (action: 'approve' | 'reject') => {
    if (!detail || decisionInFlight.current || retrying) return;
    decisionInFlight.current = true;
    setDecisionPending(action === 'approve' ? 'approved' : 'rejected');
    if (action === 'reject') {
      lifecycleEpoch.current += 1;
      if (debounceTimer.current !== null) {
        window.clearTimeout(debounceTimer.current);
        debounceTimer.current = null;
      }
    } else {
      setSaving(true);
    }
    setError(null);
    try {
      let decisionDetail = detail;
      if (action === 'reject') {
        const pendingPatch = patchPromise.current;
        if (pendingPatch) await pendingPatch;
        setSaving(false);
        decisionDetail = await load();
      }
      if (decisionDetail.approvalId) {
        await approvalsApi.apiApprovalsApprovalIdDecisionPost(
          decisionDetail.approvalId,
          {
            decision: action === 'approve' ? 'approved' : 'rejected',
            note: decisionNote.trim() || undefined,
            version: { revision: decisionDetail.request.currentRevision },
          },
        );
      } else {
        const method =
          action === 'approve'
            ? workspacesApi.apiTeamsTeamIdWorkspaceRequestsTokenApprovePost.bind(
                workspacesApi,
              )
            : workspacesApi.apiTeamsTeamIdWorkspaceRequestsTokenRejectPost.bind(
                workspacesApi,
              );
        await method(numericTeamId, token, {
          revision: decisionDetail.request.currentRevision,
        });
      }
      setDecisionNote('');
      await load();
    } catch (requestError) {
      if (errorStatus(requestError) === 409) await load(true);
      else
        setError(
          errorMessage(requestError) ??
            `Could not ${action} this request. Try again.`,
        );
    } finally {
      decisionInFlight.current = false;
      setSaving(false);
      setDecisionPending(null);
    }
  };

  if (loading)
    return <div className="workspace-approval-card">Loading request…</div>;
  if (!detail || !draft)
    return (
      <div className="workspace-approval-card workspace-resource-error">
        {error}
      </div>
    );

  const pending = detail.request.status === 'pending';
  const hasUnsavedChanges = !sameAllocation(draft, serverAllocation.current);
  const quote = detail.revision.quote as WorkspaceQuote;
  const summaryLine = describeAllocation(
    serverAllocation.current ?? draft,
    quote,
  );
  const gpu = draft.gpu;
  type GpuCapability = {
    type?: string;
    minCount?: number;
    maxCount?: number;
    step?: number;
    allowedCounts?: number[];
  };
  const gpuCapabilities = (capabilities?.gpuTypes || []) as GpuCapability[];
  const gpuCapability = gpu
    ? gpuCapabilities.find((candidate) => candidate.type === gpu.type)
    : gpuCapabilities[0];

  const update = (change: Partial<Allocation>) => {
    if (decisionInFlight.current) return;
    editVersion.current += 1;
    setDraft((current) => (current ? { ...current, ...change } : current));
  };

  const renderRange = (
    key: 'cpuMillis' | 'memoryMiB' | 'storageGiB',
    label: string,
    format: (value: number) => string,
  ) => {
    const range = capabilities?.[key];
    const value = Number(draft[key] || range?.min || 0);
    return pending && range ? (
      <div className="workspace-range-row" key={key}>
        <div className="workspace-range-heading">
          <label htmlFor={`workspace-request-${token}-${key}`}>{label}</label>
          <output htmlFor={`workspace-request-${token}-${key}`}>
            {format(value)}
          </output>
        </div>
        <input
          id={`workspace-request-${token}-${key}`}
          type="range"
          aria-label={label}
          min={range.min}
          max={range.max}
          step={range.step}
          list={`workspace-request-${token}-${key}-steps`}
          value={value}
          disabled={deciding}
          onChange={(event) =>
            update({ [key]: snapToStep(Number(event.target.value), range) })
          }
        />
        <datalist id={`workspace-request-${token}-${key}-steps`}>
          <option value={range.min} />
          <option value={range.max} />
        </datalist>
      </div>
    ) : (
      <div className="workspace-resource-readout" key={key}>
        <span>{label.replace('Suggested ', '')}</span>
        <strong>{format(value)}</strong>
      </div>
    );
  };

  const editorBody = (
    <>
      {pending && mode === 'edit' && (
        <button
          type="button"
          className="lightButton workspace-back-button"
          disabled={deciding}
          onClick={() => setMode('summary')}>
          ← Back to summary
        </button>
      )}

      <div className="workspace-range-matrix workspace-approval-matrix">
        {renderRange('cpuMillis', 'Suggested CPU', formatCpu)}
        {renderRange('memoryMiB', 'Suggested memory', formatMemory)}
        {renderRange('storageGiB', 'Suggested storage', formatStorage)}

        {pending ? (
          <div className="workspace-request-options">
            <label className="workspace-toggle-row workspace-toggle-row-compact">
              <span>
                <strong>GPU</strong>
                <small>Add accelerator capacity.</small>
              </span>
              <input
                type="checkbox"
                aria-label="Suggested GPU"
                checked={!!gpu}
                disabled={deciding || !gpuCapability}
                onChange={(event) =>
                  update({
                    gpu: event.target.checked
                      ? {
                          type: gpuCapability?.type || '',
                          count: gpuCapability?.minCount || 1,
                        }
                      : null,
                  })
                }
              />
            </label>
            {!!gpu && gpuCapability && (
              <>
                {gpuCapabilities.length > 1 && (
                  <label className="workspace-select-row">
                    <span>GPU type</span>
                    <select
                      aria-label="Suggested GPU type"
                      value={gpu.type}
                      disabled={deciding}
                      onChange={(event) => {
                        const selected = gpuCapabilities.find(
                          (candidate) => candidate.type === event.target.value,
                        );
                        const count =
                          selected?.allowedCounts?.[0] ||
                          selected?.minCount ||
                          1;
                        update({ gpu: { type: event.target.value, count } });
                      }}>
                      {gpuCapabilities.map((candidate) => (
                        <option key={candidate.type} value={candidate.type}>
                          {candidate.type}
                        </option>
                      ))}
                    </select>
                  </label>
                )}
                <label className="workspace-stepper">
                  <span>GPU count</span>
                  {gpuCapability.allowedCounts?.length ? (
                    <select
                      aria-label="Suggested GPU count"
                      value={gpu.count || 1}
                      disabled={deciding}
                      onChange={(event) =>
                        update({
                          gpu: { ...gpu, count: Number(event.target.value) },
                        })
                      }>
                      {gpuCapability.allowedCounts.map((count) => (
                        <option key={count} value={count}>
                          {count}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="number"
                      aria-label="Suggested GPU count"
                      min={gpuCapability.minCount || 1}
                      max={gpuCapability.maxCount || 1}
                      step={gpuCapability.step || 1}
                      value={gpu.count || 1}
                      disabled={deciding}
                      onChange={(event) =>
                        update({
                          gpu: { ...gpu, count: Number(event.target.value) },
                        })
                      }
                    />
                  )}
                </label>
              </>
            )}
            <label className="workspace-select-row">
              <span>Network</span>
              <select
                aria-label="Suggested network"
                value={draft.networkMode || 'none'}
                disabled={deciding}
                onChange={(event) =>
                  update({
                    networkMode: event.target
                      .value as Allocation['networkMode'],
                  })
                }>
                {(capabilities?.networkModes || ['none']).map(
                  (networkModeOption) => (
                    <option key={networkModeOption} value={networkModeOption}>
                      {networkModeOption === 'none'
                        ? 'No network'
                        : 'Allowlisted egress'}
                    </option>
                  ),
                )}
              </select>
            </label>
            <label className="workspace-stepper">
              <span>Projected active hours per day</span>
              <input
                type="number"
                aria-label="Projected active hours per day"
                min={1}
                max={24}
                step={1}
                value={draft.projectionHoursPerDay || 8}
                disabled={deciding}
                onChange={(event) =>
                  update({ projectionHoursPerDay: Number(event.target.value) })
                }
              />
            </label>
          </div>
        ) : (
          <>
            <div className="workspace-resource-readout">
              <span>GPU</span>
              <strong>
                {gpu ? `${gpu.count || 1} × ${gpu.type}` : 'None'}
              </strong>
            </div>
            <div className="workspace-resource-readout">
              <span>Network</span>
              <strong>
                {draft.networkMode === 'allowlisted_egress'
                  ? 'Allowlisted egress'
                  : 'None'}
              </strong>
            </div>
          </>
        )}
      </div>

      <section className="workspace-quote" aria-label="Projected price">
        <div className="workspace-quote-total">
          <span>Projected 30-day cost</span>
          <strong>
            {detail.revision.providerKey === 'local'
              ? 'No provider charge'
              : quote.label || `$${quote.total30DayUsd || '—'}`}
          </strong>
        </div>
        {!!quote.lineItems?.length && (
          <dl>
            {quote.lineItems.map((line, index) => (
              <div key={`${line.kind}-${index}`}>
                <dt>{line.kind}</dt>
                <dd>${line.total30DayUsd || '0.00'}</dd>
              </div>
            ))}
          </dl>
        )}
      </section>

      <p className="workspace-idle-note">
        Compute turns off after ten minutes when idle. Files remain and restore
        on the next session.
      </p>
      {saving && (
        <p className="workspace-save-note" role="status">
          Refreshing quote…
        </p>
      )}
      {notice && (
        <p className="workspace-resource-notice" role="status">
          {notice}
        </p>
      )}
      {(error || retrying) && (
        <div className="workspace-resource-error" role="alert">
          <span>{error}</span>
          {pending && hasUnsavedChanges && (
            <AsyncButton
              type="button"
              className="lightButton workspace-retry-button"
              disabled={retrying || deciding}
              loading={retrying}
              loadingText="Retrying..."
              onClick={() => {
                setRetrying(true);
                setRetryNonce((value) => value + 1);
              }}>
              Retry update
            </AsyncButton>
          )}
        </div>
      )}

      {pending && (
        <>
          <label className="workspace-decision-note">
            <span>Note to agent (optional)</span>
            <textarea
              aria-label="Note to agent"
              maxLength={4000}
              disabled={deciding}
              value={decisionNote}
              onChange={(event) => setDecisionNote(event.target.value)}
            />
          </label>
          <footer className="workspace-approval-actions">
            <AsyncButton
              type="button"
              className="lightButton"
              disabled={deciding || retrying}
              loading={decisionPending === 'rejected'}
              loadingText="Rejecting..."
              onClick={() => decideGuard.run('reject')}>
              Reject
            </AsyncButton>
            <AsyncButton
              type="button"
              disabled={saving || deciding || retrying || hasUnsavedChanges}
              loading={decisionPending === 'approved'}
              loadingText="Approving..."
              onClick={() => decideGuard.run('approve')}>
              Approve &amp; provision
            </AsyncButton>
          </footer>
        </>
      )}
    </>
  );

  const summaryBody = (
    <>
      <p className="workspace-summary-line">{summaryLine}</p>
      {confirming ? (
        <>
          {error && (
            <div className="workspace-resource-error" role="alert">
              <span>{error}</span>
            </div>
          )}
          <label className="workspace-decision-note">
            <span>Note to agent (optional)</span>
            <textarea
              aria-label="Note to agent"
              maxLength={4000}
              disabled={deciding}
              value={decisionNote}
              onChange={(event) => setDecisionNote(event.target.value)}
            />
          </label>
          <footer className="workspace-summary-actions">
            <button
              type="button"
              className="lightButton"
              disabled={deciding}
              onClick={() => setConfirming(null)}>
              Cancel
            </button>
            <AsyncButton
              type="button"
              disabled={
                deciding ||
                retrying ||
                (confirming === 'approve' && (saving || hasUnsavedChanges))
              }
              loading={decisionPending !== null}
              loadingText={
                confirming === 'approve' ? 'Approving...' : 'Rejecting...'
              }
              onClick={() => decideGuard.run(confirming)}>
              Confirm
            </AsyncButton>
          </footer>
        </>
      ) : (
        <footer className="workspace-summary-actions">
          <button
            type="button"
            className="lightButton"
            disabled={deciding || retrying}
            onClick={() => setConfirming('reject')}>
            Reject
          </button>
          <button
            type="button"
            className="lightButton"
            disabled={deciding}
            onClick={() => setMode('edit')}>
            Edit
          </button>
          <button
            type="button"
            disabled={deciding || retrying || saving || hasUnsavedChanges}
            onClick={() => setConfirming('approve')}>
            Approve
          </button>
        </footer>
      )}
    </>
  );

  // Once decided, the card is a historical record: the header already
  // carries the token, requesting agent, and status pill — the body is
  // just the one-line description the request summary used.
  const decidedBody = <p className="workspace-summary-line">{summaryLine}</p>;

  return (
    <article
      className="workspace-approval-card"
      aria-labelledby={`request-${token}-title`}>
      {!headerless && (
        <ApprovalCardHeader
          teamId={teamId}
          agent={detail.requestingAgent}
          requestingUser={detail.requestingUser}
          requestingTrigger={detail.requestingTrigger}
          origin={detail.origin}
          createdAt={detail.request.createdAt}
        />
      )}
      <header className="workspace-approval-header">
        <div>
          <span className="workspace-approval-eyebrow">Workspace request</span>
          <h3 id={`request-${token}-title`}>{token}</h3>
        </div>
        <span
          className={`workspace-request-status workspace-request-status-${detail.request.status}`}>
          {STATUS_LABELS[detail.request.status] || detail.request.status}
        </span>
      </header>

      {!pending ? decidedBody : mode === 'summary' ? summaryBody : editorBody}
      {decideGuard.denialModal}
    </article>
  );
};

export default WorkspaceApprovalCard;
